import React, { useMemo, useState } from 'react';
import type { SchemaResult, TableSchema, FieldMetadata } from '../../services/schemaQueryApi';
import { ChevronDown, ChevronRight, Link2, Database, Table2 } from 'lucide-react';
import { formatRowCount } from '../../utils/numberFormat';

/** Temp/staging tables the analyst may want to hide from module results. */
function isTempTable(name: string): boolean {
  return /tmp|temp/i.test(name);
}

interface SchemaViewerProps {
  schema: SchemaResult;
}

function formatConstraints(constraints: string[]): string {
  if (!constraints || constraints.length === 0) return '-';
  return constraints
    .map((c) => {
      if (c === 'PRIMARY_KEY') return 'PK';
      if (c === 'FOREIGN_KEY') return 'FK';
      if (c === 'NOT_NULL') return 'NOT NULL';
      if (c === 'UNIQUE') return 'UNIQUE';
      return c;
    })
    .join(', ');
}

/** Renders one table's fields. */
function TableFields({ table }: { table: TableSchema }) {
  const [expandedFields, setExpandedFields] = useState<Set<string>>(new Set());

  const toggleFieldExpand = (fieldName: string) => {
    setExpandedFields((prev) => {
      const next = new Set(prev);
      if (next.has(fieldName)) next.delete(fieldName);
      else next.add(fieldName);
      return next;
    });
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 border-b border-gray-200">
          <tr>
            <th className="px-4 py-2 text-left font-semibold text-gray-700">Field</th>
            <th className="px-4 py-2 text-left font-semibold text-gray-700">Type</th>
            <th className="px-4 py-2 text-left font-semibold text-gray-700">Constraints</th>
            <th className="px-4 py-2 text-left font-semibold text-gray-700">Description</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {table.fields.map((field: FieldMetadata) => {
            const isExpanded = expandedFields.has(field.name);
            const hasLongDescription = !!field.description && field.description.length > 50;
            return (
              <React.Fragment key={field.name}>
                <tr className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-2 font-mono text-sm font-medium text-gray-900">{field.name}</td>
                  <td className="px-4 py-2 font-mono text-xs text-gray-600">{field.type || '-'}</td>
                  <td className="px-4 py-2">
                    {field.constraints && field.constraints.length > 0 ? (
                      <span className="inline-block px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs font-medium">
                        {formatConstraints(field.constraints)}
                      </span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-gray-700">
                    {hasLongDescription ? (
                      <button
                        onClick={() => toggleFieldExpand(field.name)}
                        className="flex items-center gap-1.5 text-gray-700 hover:text-blue-600 transition-colors text-left"
                      >
                        {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        <span className={isExpanded ? '' : 'line-clamp-1'}>{field.description}</span>
                      </button>
                    ) : (
                      field.description || '-'
                    )}
                  </td>
                </tr>
              </React.Fragment>
            );
          })}
        </tbody>
      </table>

      {table.relatedTables && table.relatedTables.length > 0 && (
        <div className="border-t border-gray-200 bg-gray-50 p-3">
          <div className="flex items-center gap-2 mb-2">
            <Link2 size={14} className="text-gray-600" />
            <h4 className="text-xs font-semibold text-gray-900">Related Tables</h4>
          </div>
          <div className="space-y-1">
            {table.relatedTables.map((rel, idx) => (
              <div key={idx} className="flex items-center gap-2 text-xs">
                <span className="font-mono text-blue-600">{rel.via_column}</span>
                <span className="text-gray-400">→</span>
                <span className="font-mono text-purple-600">{rel.table_name}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/** A single collapsible table card. */
function TableCard({ table, defaultOpen }: { table: TableSchema; defaultOpen: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 px-4 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
      >
        <div className="flex items-center gap-2 min-w-0">
          {open ? <ChevronDown size={16} className="text-gray-500 flex-shrink-0" /> : <ChevronRight size={16} className="text-gray-500 flex-shrink-0" />}
          <Table2 size={14} className="text-blue-600 flex-shrink-0" />
          <span className="font-mono font-semibold text-xs text-gray-900 flex-shrink-0">{table.table_name}</span>
          {table.description && (
            <span className="text-xs text-gray-500 truncate hidden sm:inline">— {table.description}</span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {typeof table.row_count === 'number' && (
            <span className="text-[10px] text-gray-500" title={`${table.row_count.toLocaleString()} rows`}>
              {formatRowCount(table.row_count)} rows
            </span>
          )}
          <span className="inline-block px-2 py-0.5 bg-gray-200 text-gray-600 rounded text-[10px]">
            {table.fields.length} field{table.fields.length === 1 ? '' : 's'}
          </span>
        </div>
      </button>
      {open && (
        <div>
          {table.description && (
            <div className="px-4 py-2 bg-white border-b border-gray-100 text-xs text-gray-600">
              {table.description}
            </div>
          )}
          {(table.primary_key || table.database) && (
            <div className="px-4 py-1.5 bg-white border-b border-gray-100 text-[11px] text-gray-500 flex gap-4">
              {table.primary_key && (
                <span>PK: <span className="font-mono text-gray-700">{table.primary_key}</span></span>
              )}
              {table.database && (
                <span>DB: <span className="font-mono text-gray-700">{table.database}</span></span>
              )}
            </div>
          )}
          <TableFields table={table} />
        </div>
      )}
    </div>
  );
}

/**
 * Renders a resolved schema. For a module, shows a header and one collapsible
 * card per table, sorted by row count (empty tables last), with an optional
 * temp-table filter. For a single table, shows just that table (expanded).
 */
export function SchemaViewer({ schema }: SchemaViewerProps) {
  const isModule = schema.kind === 'module';
  const [excludeTemp, setExcludeTemp] = useState(false);

  const tempCount = useMemo(
    () => schema.tables.filter((t) => isTempTable(t.table_name)).length,
    [schema.tables]
  );

  // Sort by row count descending so empty/unused tables sink to the bottom;
  // tables with no count sort below 0-row tables.
  const visibleTables = useMemo(() => {
    const filtered = excludeTemp
      ? schema.tables.filter((t) => !isTempTable(t.table_name))
      : schema.tables;
    return [...filtered].sort((a, b) => (b.row_count ?? -1) - (a.row_count ?? -1));
  }, [schema.tables, excludeTemp]);

  const totalFields = visibleTables.reduce((sum, t) => sum + t.fields.length, 0);

  return (
    <div className="w-full space-y-2">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg p-3">
        <div className="flex items-center gap-2">
          <Database size={16} />
          <h3 className="text-base font-semibold">{schema.title}</h3>
          <span className="text-[10px] uppercase tracking-wide bg-white/20 px-1.5 py-0.5 rounded">
            {isModule ? 'Module' : 'Table'}
          </span>
        </div>
        <div className="flex items-center justify-between gap-2 mt-1">
          <p className="text-xs text-blue-100">
            {isModule
              ? `${visibleTables.length} table${visibleTables.length === 1 ? '' : 's'} · ${totalFields} field${totalFields === 1 ? '' : 's'}`
              : `${totalFields} field${totalFields === 1 ? '' : 's'}`}
          </p>
          {isModule && tempCount > 0 && (
            <label className="flex items-center gap-1.5 text-xs text-blue-100 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={excludeTemp}
                onChange={(e) => setExcludeTemp(e.target.checked)}
                className="cursor-pointer"
              />
              Exclude temp tables ({tempCount})
            </label>
          )}
        </div>
      </div>

      {/* Table cards. For a module with many tables, expand the first only. */}
      <div className="space-y-2">
        {visibleTables.map((table, idx) => (
          <TableCard key={table.table_name} table={table} defaultOpen={!isModule || visibleTables.length <= 3 || idx === 0} />
        ))}
      </div>
    </div>
  );
}
